import { BarChart3, AlertTriangle } from 'lucide-react';
import { useEvaluateMetric, useCustomMetric } from '../../../hooks/useDashboards';
import type { WidgetCommonProps } from './KpiCardWidget';

/**
 * Dashboard widget that renders a user-defined custom metric.
 *
 * The widget config should contain `customMetricId` to identify which
 * custom metric definition to evaluate.
 */
export function CustomMetricWidget({ config, loading: parentLoading, dateRange }: WidgetCommonProps) {
  const metricId = config?.customMetricId as string | undefined;

  const { data: definition, isLoading: defLoading } = useCustomMetric(metricId || '');
  const { data: result, isLoading: evalLoading } = useEvaluateMetric(
    metricId || '',
    dateRange?.start,
    dateRange?.end,
  );

  const isLoading = parentLoading || defLoading || evalLoading;

  // No metric configured
  if (!metricId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-2 p-4">
        <BarChart3 className="w-8 h-8" />
        <p className="text-sm text-center">
          No metric configured. Edit this widget to select a custom metric.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3 p-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      </div>
    );
  }

  // Error state
  if (result?.error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400 gap-2 p-4">
        <AlertTriangle className="w-6 h-6" />
        <p className="text-sm text-center">{result.error}</p>
      </div>
    );
  }

  const name = definition?.name || config?.title || 'Custom Metric';
  const unit = definition?.unit || '';
  const displayFormat = definition?.displayFormat || 'number';

  // Render based on result type
  if (result?.type === 'comparison' && result.metrics) {
    return (
      <div className="p-4 h-full flex flex-col">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">{name}</p>
        <div className="flex-1 space-y-2">
          {result.metrics.map((m: { label: string; value: number }, i: number) => {
            const max = Math.max(...(result.metrics || []).map((x: any) => x.value), 1);
            const pct = (m.value / max) * 100;
            return (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 truncate">{m.label}</span>
                  <span className="font-semibold text-gray-900 dark:text-white ml-2">{m.value}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {result.successRate != null && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
            Success Rate: <span className="font-semibold">{result.successRate}%</span>
            {' '}&middot;{' '}Total: {result.total}
          </p>
        )}
      </div>
    );
  }

  // Single value display
  const value = result?.value ?? 0;
  const formattedValue = formatValue(value, displayFormat, unit);

  return (
    <div className="p-4 h-full flex flex-col justify-center">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{name}</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">{formattedValue}</p>
      {unit && displayFormat !== 'percentage' && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{unit}</p>
      )}
    </div>
  );
}

function formatValue(value: number, format: string, unit: string): string {
  switch (format) {
    case 'percentage':
      return `${value}%`;
    case 'duration':
      if (value < 1) return `${Math.round(value * 60)}m`;
      return `${value.toFixed(1)}h`;
    case 'currency':
      return `$${value.toLocaleString()}`;
    default:
      return value.toLocaleString();
  }
}
